// Top-level numbers for a finished discovery. Reads job_metrics first
// (worker-supplied, authoritative) and falls back to deriving from the
// result graph if the worker didn't ship metrics for some reason.

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { Discovery } from '@/lib/discoveries';
import type * as React from 'react';

function fmtDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

function metric(label: string, value: React.ReactNode) {
  return (
    <Card>
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl tabular-nums">{value}</CardTitle>
      </CardHeader>
    </Card>
  );
}

export function MetricsCard({ discovery }: { discovery: Discovery }) {
  const m = discovery.job_metrics ?? {};
  const nNodes = (m.n_nodes as number | undefined) ?? discovery.result?.nodes?.length ?? '—';
  const nDirected =
    (m.n_directed_edges as number | undefined) ?? discovery.result?.edges?.length ?? '—';
  const isDag = m.is_dag === undefined ? '—' : m.is_dag ? 'Yes' : 'No';

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {metric('Nodes', nNodes)}
      {metric('Directed edges', nDirected)}
      {metric('Is DAG', isDag)}
      {metric('Duration', fmtDuration(discovery.duration_ms))}
    </div>
  );
}

interface MetricsPlaceholderProps {
  message: string;
}

export function MetricsPlaceholder({ message }: MetricsPlaceholderProps) {
  return (
    <Card>
      <CardHeader>
        <CardDescription>Status</CardDescription>
        <CardTitle className="text-base font-normal">{message}</CardTitle>
      </CardHeader>
      <CardContent />
    </Card>
  );
}
